import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTicket() {
  const { serial } = useParams();
  const [disp, setDisp] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", reported_by: "", reporter_contact: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/dispenser/${serial}`)
      .then((r) => setDisp(r.data))
      .catch(() => setErr("Erogatore non trovato. Verifica il QR code."));
  }, [serial]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/tickets`, { ...form, dispenser_serial: serial });
      setDone(data);
    } catch (er) {
      setErr(er?.response?.data?.detail || "Errore nell'invio");
    } finally { setLoading(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="h-12 w-12 mx-auto rounded-sm bg-red-100 text-red-700 flex items-center justify-center mb-4">✕</div>
        <h1 className="font-display text-2xl font-bold text-zinc-950 mb-2">{err}</h1>
        <p className="text-sm text-zinc-500">Contatta l&apos;assistenza Wineemotion al numero verde.</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-4" strokeWidth={1.5} />
        <h1 className="font-display text-3xl font-bold text-zinc-950 mb-2">Ticket inviato!</h1>
        <div className="text-sm text-zinc-500">Numero riferimento: <span className="font-mono text-zinc-900">{done.ticket_number}</span></div>
        <p className="text-sm text-zinc-600 mt-3">Un tecnico Wineemotion prenderà in carico la richiesta al più presto.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-lg mx-auto p-6 sm:p-10">
        <div className="flex items-center gap-2 mb-8">
          <BrandLogo size="xl" />
          <div>
            <div className="font-display font-bold text-zinc-950 leading-none">Wineemotion · Assistenza</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Apri un ticket</div>
          </div>
        </div>

        {disp && (
          <div className="border border-zinc-200 rounded-sm bg-white p-4 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Erogatore rilevato</div>
            <div className="mt-1 font-mono text-sm text-zinc-900">{disp.serial_number}</div>
            <div className="text-sm font-medium text-zinc-900">{disp.model}</div>
            {disp.customer_name && <div className="text-xs text-zinc-500 mt-1">{disp.customer_name}{disp.location ? ` · ${disp.location}` : ""}</div>}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Il tuo nome *</Label>
            <Input required data-testid="public-name" value={form.reported_by} onChange={(e) => setForm({ ...form, reported_by: e.target.value })} className="mt-1 rounded-sm h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Telefono / email</Label>
            <Input data-testid="public-contact" value={form.reporter_contact} onChange={(e) => setForm({ ...form, reporter_contact: e.target.value })} className="mt-1 rounded-sm h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Cosa non funziona? *</Label>
            <Input required data-testid="public-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm h-11" placeholder="es. Il vino esce lentamente" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Descrizione dettagliata *</Label>
            <Textarea required data-testid="public-description" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <Button type="submit" data-testid="public-submit" disabled={loading || !disp} className="w-full h-11 bg-brand hover:bg-brand-dark text-white rounded-sm font-medium">
            {loading ? "Invio..." : "Invia richiesta"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}
